// /set-password?token=... — final step of the welcome-email flow.
//
// Token comes from the welcome email link. Backend validates the token
// against the staff row, hashes the password, mints a session cookie,
// returns scopes. We then navigate to /dashboard.

import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth.js';
import { friendly_error } from '../lib/friendly-error.js';

export function SetPasswordPage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { set_password } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <main className="min-h-screen bg-bg text-ink flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-surface border border-line rounded-lg p-6 shadow-sm text-center">
          <h1 className="text-lg font-medium text-bad mb-2">Missing token</h1>
          <p className="text-sm text-ink/70">
            This link is incomplete. Please use the link from your welcome email,
            or go to the login page and click "Forgot password?".
          </p>
        </div>
      </main>
    );
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/\d/.test(password) || !/[a-zA-Z]/.test(password)) {
      setError('Password must include both letters and digits.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await set_password(token, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(friendly_error(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-bg text-ink flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-surface border border-line rounded-lg p-6 shadow-sm">
        <div className="text-center mb-6">
          <h1 className="font-semibold tracking-tight text-3xl text-primary mb-1">ChitDesk</h1>
          <p className="text-sm text-ink/70">Set your password</p>
          <p className="text-xs text-ink/50 mt-1 font-mono break-all">
            {typeof window !== 'undefined' ? window.location.hostname : ''}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <label className="text-sm">
            <span className="block mb-1">New password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              minLength={8}
              maxLength={200}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Confirm password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              maxLength={200}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <p className="text-xs text-ink/50">At least 8 characters; mix of letters and digits.</p>

          <button
            type="submit"
            disabled={busy}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white py-2 rounded font-medium text-sm transition mt-2"
          >
            {busy ? 'Setting password…' : 'Set password and sign in'}
          </button>
        </div>

        {error && (
          <div className="mt-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
            {error}
          </div>
        )}
      </form>
    </main>
  );
}
